<template>
  <div v-if="pageCount > 1" ref="container" class="results-pagination-container">
    <nav class="results-pagination" :class="containerModifiers" :aria-label="resultText('pagination_label')">
      <div class="results-pagination__pages">
        <sp-button
          v-if="page > 2"
          class="results-pagination__item"
          variant="outlined"
          :data-testid="TestId.finder.paginationFirst"
          @click="showFirstPage"
          >{{ resultText("pagination_first") }}</sp-button
        >
        <sp-button
          class="results-pagination__item"
          variant="outlined"
          :aria-label="resultText('previous')"
          :class="disabledModifier(isFirstPage)"
          :disabled="isFirstPage"
          :data-testid="TestId.finder.paginationPrev"
          @click="showPreviousPage"
          ><sp-icon class="results-pagination__icon" :name="prevIcon"></sp-icon
        ></sp-button>
        <sp-button
          v-for="entry in pages"
          :key="entry.number"
          class="results-pagination__item"
          :class="currentModifier(entry.current)"
          :variant="entry.variant"
          :aria-current="entry.current"
          :data-page="entry.number"
          :data-testid="TestId.finder.paginationPage"
          @click="showClickedPage"
          >{{ entry.number }}</sp-button
        >
        <sp-button
          class="results-pagination__item"
          variant="outlined"
          :aria-label="resultText('next')"
          :class="disabledModifier(isLastPage)"
          :disabled="isLastPage"
          :data-testid="TestId.finder.paginationNext"
          @click="showNextPage"
          ><sp-icon class="results-pagination__icon" :name="nextIcon"></sp-icon
        ></sp-button>
      </div>
      <div class="results-pagination__sizes">
        <small class="results-pagination__label">{{ resultText("results_per_page") }}</small>
        <sp-button
          v-for="size in PER_PAGE_SIZES"
          :key="size"
          class="results-pagination__item results-pagination__size"
          :class="currentModifier(perPage === size)"
          :variant="sizeVariant(size)"
          :pressed="perPage === size"
          :data-per-page="size"
          :data-testid="TestId.finder.paginationPerPage"
          @click="showClickedPageSize"
          >{{ size }}</sp-button
        >
      </div>
    </nav>
  </div>
</template>

<script setup>
import { computed, useTemplateRef } from "vue";
import { TestId } from "#/test-ids";
import { useContainerBreakpoints } from "#ce/composables/useContainerBreakpoints";
import { resultText } from "#ce/components/SpFinder/view-models/result-text";

const PER_PAGE_SIZES = [10, 20];
const WINDOW_WIDTH = 5;

const props = defineProps({
  page: {
    type: Number,
    required: true,
  },
  pageCount: {
    type: Number,
    required: true,
  },
  perPage: {
    type: Number,
    default: undefined,
  },
  prevIcon: {
    type: String,
    default: "chevron-left-filled",
  },
  nextIcon: {
    type: String,
    default: "chevron-right-filled",
  },
});

const emit = defineEmits(["show-page", "show-per-page"]);

const container = useTemplateRef("container");
const { containerModifiers } = useContainerBreakpoints(container, {
  small: { max: 750 },
  medium: { min: 751, max: 900 },
  large: { min: 901 },
});

const pages = computed(() => {
  const entries = [];
  let number = Math.max(props.page - Math.floor(WINDOW_WIDTH / 2), 1);
  while (number <= props.pageCount && entries.length < WINDOW_WIDTH) {
    const current = number === props.page;
    entries.push({ number, current: current ? "page" : undefined, variant: current ? "flat" : "outlined" });
    number += 1;
  }
  return entries;
});
const isFirstPage = computed(() => props.page <= 1);
const isLastPage = computed(() => props.page >= props.pageCount);

function disabledModifier(disabled) {
  return { "--disabled": disabled };
}

function currentModifier(current) {
  return { "--current": Boolean(current) };
}

function sizeVariant(size) {
  return props.perPage === size ? "flat" : "outlined";
}

function showPage(page) {
  emit("show-page", page);
}

function showAdjacentPage(step) {
  const page = props.page + step;
  if (page >= 1 && page <= props.pageCount) {
    showPage(page);
  }
}

function showFirstPage() {
  showPage(1);
}

function showPreviousPage() {
  showAdjacentPage(-1);
}

function showNextPage() {
  showAdjacentPage(1);
}

function showClickedPage(event) {
  showPage(Number(event.currentTarget.dataset.page));
}

function showClickedPageSize(event) {
  emit("show-per-page", Number(event.currentTarget.dataset.perPage));
}
</script>

<style lang="scss" scoped>
@use "@assets/mixins/breakpoint" as *;

.results-pagination {
  --padding-block: var(--sp-comp-finder-results-pagination-padding-block, 20px);
  --padding-inline: var(--sp-comp-finder-results-pagination-padding-inline, 10px);
  --padding-inline-lg: var(--sp-comp-finder-results-pagination-padding-inline-lg, var(--padding-inline));
  --gap: var(--sp-comp-finder-results-pagination-gap, 20px);
  --results-pagination-pages-gap: var(--sp-comp-finder-results-pagination-pages-gap, 0);
  --max-width: var(--sp-comp-finder-results-pagination-max-width, 1140px);
  --item-height: var(--sp-comp-finder-results-pagination-item-height, var(--sp-ref-scale-40));
  --item-min-width: var(--sp-comp-finder-results-pagination-item-min-width, var(--item-height));
  --item-padding: var(--sp-comp-finder-results-pagination-item-padding, 0 10px);
  --item-radius: var(--sp-comp-finder-results-pagination-item-border-radius, 5px);
  // The corners that face a neighbouring page button.
  --results-pagination-item-joined-radius: var(--sp-comp-finder-results-pagination-item-joined-border-radius, 0px);
  // The border width and radii need a unit, `0px` and not `0`: `calc()` cannot subtract a bare number from a length.
  --results-pagination-item-inner-radius: calc(var(--item-radius) - var(--item-border-width));
  --results-pagination-item-inner-joined-radius: calc(
    var(--results-pagination-item-joined-radius) - var(--item-border-width)
  );
  --item-color: var(--sp-comp-finder-results-pagination-item-color, var(--sp-sys-color-primary));
  --item-background: var(--sp-comp-finder-results-pagination-item-background, var(--sp-sys-color-surface));
  --item-border-width: var(--sp-comp-finder-results-pagination-item-border-width, 1px);
  --item-border-color: var(--sp-comp-finder-results-pagination-item-border-color, var(--sp-sys-color-outline));
  --item-disabled-color: var(--sp-comp-finder-results-pagination-item-disabled-color, var(--sp-sys-color-disabled));
  --item-font: var(--sp-comp-finder-results-pagination-item-font, var(--sp-sys-typescale-small-medium-short));
  --item-font-size: var(--sp-comp-finder-results-pagination-item-font-size, var(--sp-sys-typescale-small-font-size));
  --item-active-background: var(--sp-comp-finder-results-pagination-item-active-background, var(--item-color));
  --item-active-color: var(--sp-comp-finder-results-pagination-item-active-color, var(--item-background));
  --item-active-border-color: var(--sp-comp-finder-results-pagination-item-active-border-color, var(--item-color));
  --item-active-font: var(--sp-comp-finder-results-pagination-item-active-font, var(--item-font));
  --item-hover-background: var(--sp-comp-finder-results-pagination-item-hover-background, var(--item-color));
  --item-hover-color: var(--sp-comp-finder-results-pagination-item-hover-color, var(--item-background));
  --item-hover-border-color: var(--sp-comp-finder-results-pagination-item-hover-border-color, var(--item-color));
  --indicator-height: var(--sp-comp-finder-results-pagination-indicator-height, 0px);
  --indicator-color: var(--sp-comp-finder-results-pagination-indicator-color, var(--sp-sys-color-secondary));
  --indicator-radius: var(--sp-comp-finder-results-pagination-indicator-radius, 10px 10px 0 0);
  --label-color: var(--sp-comp-finder-results-pagination-label-color, var(--sp-sys-color-on-surface));
  --label-font: var(--sp-comp-finder-results-pagination-label-font, var(--item-font));
  --label-font-size: var(--sp-comp-finder-results-pagination-label-font-size, var(--item-font-size));

  box-sizing: border-box;
  width: 100%;
  max-width: var(--sp-comp-finder-results-site-width, 100%);
  margin-inline: auto;
  padding: var(--padding-block) var(--padding-inline);

  // A site whose container follows its own breakpoints sets --sp-comp-finder-results-site-width, which then wins.
  @include sp-breakpoint(sm) {
    max-width: var(--sp-comp-finder-results-site-width, var(--sp-comp-finder-results-pagination-max-width-sm, 751px));
  }

  @include sp-breakpoint(md) {
    max-width: var(--sp-comp-finder-results-site-width, var(--sp-comp-finder-results-pagination-max-width-md, 992px));
  }

  @include sp-breakpoint(lg) {
    max-width: var(--sp-comp-finder-results-site-width, var(--max-width));
    padding-inline: var(--padding-inline-lg);
  }

  @include sp-breakpoint(xl) {
    padding-inline: var(--sp-comp-finder-results-pagination-padding-inline-xl, var(--padding-inline-lg));
  }

  &.--is-medium-container,
  &.--is-large-container {
    display: grid;
    grid-template-columns: 1fr 2fr max-content;
    grid-template-areas: "left pages right";
    gap: var(--gap);
    padding-block: var(--sp-comp-finder-results-pagination-grid-padding-block, var(--padding-block));
  }

  &.--is-large-container {
    grid-template-columns: minmax(max-content, 1fr) min-content minmax(max-content, 1fr);
  }

  &__pages {
    display: flex;
    grid-area: pages;
    align-items: center;
    justify-content: center;
    gap: var(--results-pagination-pages-gap);
  }

  &__item {
    --sp-comp-button-size: var(--item-height);
    --sp-comp-button-compact-height: var(--item-height);
    --sp-comp-button-min-width: var(--item-height);
    --sp-comp-button-padding: var(--item-padding);
    --sp-comp-button-border-radius: var(--item-radius);
    --sp-comp-button-font-short: var(--item-font);
    --sp-comp-button-font-size: var(--item-font-size);
    --sp-comp-button-outlined-border-width: var(--item-border-width);
    --sp-comp-button-outlined-border-color: var(--item-border-color);
    --sp-comp-button-hover-outlined-border-color: var(--item-hover-border-color);
    --sp-comp-button-primary-outlined-text-color: var(--item-color);
    --sp-comp-button-primary-hover-outlined-bg-color: var(--item-hover-background);
    --sp-comp-button-primary-hover-outlined-text-color: var(--item-hover-color);
    --sp-comp-button-primary-active-outlined-bg-color: var(--item-background);
    --sp-comp-button-disabled-background-color: var(--item-disabled-color);

    position: relative;

    &:hover {
      --sp-comp-button-font-short: var(--item-active-font);
    }

    &.--current {
      --sp-comp-button-border-width: var(--item-border-width);
      --sp-comp-button-border-color: var(--item-active-border-color);
      --sp-comp-button-primary-bg-color: var(--item-active-background);
      --sp-comp-button-primary-text-color: var(--item-active-color);
      --sp-comp-button-primary-hover-bg-color: var(--item-active-background);
      --sp-comp-button-primary-hover-text-color: var(--item-active-color);
      --sp-comp-button-font-short: var(--item-active-font);
    }

    &.--current::after,
    &:hover::after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 50%;
      width: calc(var(--indicator-height) * 2);
      height: var(--indicator-height);
      margin-inline-start: calc(var(--indicator-height) * -1);
      background-color: var(--indicator-color);
      border-radius: var(--indicator-radius);
    }
  }

  &__pages &__item {
    --sp-comp-button-min-width: var(--item-min-width);
  }

  &.--is-small-container &__pages &__item {
    --sp-comp-button-min-width: var(--item-height);

    flex: 0 1 var(--item-min-width);
  }

  &__pages &__item:not(:first-child) {
    margin-inline-start: calc(var(--item-border-width) * -1);
  }

  // `sp-button` cannot derive its inner radius from a per-corner `--sp-comp-button-border-radius`, so the first and last
  // page buttons set `--sp-comp-button-inner-border-radius` as well.
  &__pages &__item:first-child:not(:last-child) {
    --sp-comp-button-border-radius: var(--item-radius) var(--results-pagination-item-joined-radius)
      var(--results-pagination-item-joined-radius) var(--item-radius);
    --sp-comp-button-inner-border-radius: var(--results-pagination-item-inner-radius)
      var(--results-pagination-item-inner-joined-radius) var(--results-pagination-item-inner-joined-radius)
      var(--results-pagination-item-inner-radius);
  }

  &__pages &__item:last-child:not(:first-child) {
    --sp-comp-button-border-radius: var(--results-pagination-item-joined-radius) var(--item-radius) var(--item-radius)
      var(--results-pagination-item-joined-radius);
    --sp-comp-button-inner-border-radius: var(--results-pagination-item-inner-joined-radius)
      var(--results-pagination-item-inner-radius) var(--results-pagination-item-inner-radius)
      var(--results-pagination-item-inner-joined-radius);
  }

  &__pages &__item:not(:first-child, :last-child) {
    --sp-comp-button-border-radius: var(--results-pagination-item-joined-radius);
  }

  &__sizes {
    display: flex;
    grid-area: right;
    align-items: center;
    justify-content: var(--sp-comp-finder-results-pagination-sizes-justify, center);
    margin: var(--sp-comp-finder-results-pagination-sizes-margin, var(--padding-block) 0);

    @include sp-breakpoint(md) {
      justify-content: flex-end;
      margin: 0;
    }
  }

  &__icon {
    --sp-comp-icon-size: var(--sp-sys-icon-size-medium);
    --sp-comp-icon-fill: var(--item-color);
  }

  &__item:hover &__icon {
    --sp-comp-icon-fill: var(--item-hover-color);
  }

  &__item.--disabled &__icon {
    --sp-comp-icon-fill: var(--item-disabled-color);
  }

  &__label + &__size,
  &__size + &__size {
    margin-inline-start: 0.25em;
  }

  &__label {
    margin-inline-end: 10px;
    color: var(--label-color);
    font: var(--label-font);
    font-size: var(--label-font-size);
  }
}
</style>
